'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { XIcon, GripVerticalIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { FileManager } from '@/components/partials/files'
import { Button } from '@/components/ui/button'
import { ContentFileIcon } from '@/components/partials'
import { getResizedPictureUrl, getPictureUrl, formatFileSize } from '@/lib/utils'
import { type FileType } from '@/types/file'

const isSvg = (file: FileType) => file.type === 'image/svg+xml' || file.path.toLowerCase().endsWith('.svg')
const thumbUrl = (file: FileType, w: number, h: number) =>
  isSvg(file) ? getPictureUrl(file.path) : getResizedPictureUrl(file.path, w, h)

export type PropsType = {
  multiple?: boolean
  onlyImages?: boolean
  optimizeImageSelection?: boolean
  types?: string[]
  value: FileType | FileType[] | null
  buttonLabel?: string
  buttonLabelChange?: string
  noSelectionText?: string
  maxSelectionLimit?: number
  allowMainSelection?: boolean
  initialMainId?: string
  className?: string
  onChange: (files: FileType | FileType[] | null) => void
}

// Sortable Item Component
const SortableItem = ({
  file,
  onRemove,
  allowMainSelection = false,
  onMainSelect,
  isMain = false
}: {
  file: FileType
  onRemove: (file: FileType) => void
  allowMainSelection?: boolean
  onMainSelect?: (file: FileType) => void
  isMain?: boolean
}) => {
  const t = useTranslations()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: file.id
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative rounded-md border p-2 ${isMain ? 'border-primary ring-primary ring-1' : ''}`}>
      <button
        type="button"
        className="bg-background hover:bg-muted absolute top-1 right-1 z-10 rounded-full p-1 shadow-sm"
        onClick={() => onRemove(file)}>
        <XIcon className="h-4 w-4" />
      </button>

      <div
        {...attributes}
        {...listeners}
        className="bg-background hover:bg-muted absolute top-1 left-1 z-10 cursor-grab rounded-full p-1 shadow-sm">
        <GripVerticalIcon className="h-4 w-4" />
      </div>

      {file.type.startsWith('image/') ? (
        <PhotoView src={thumbUrl(file, 800, 600)}>
          <div className="bg-checkerboard mb-1 flex h-24 cursor-pointer items-center justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Image
                  src={thumbUrl(file, 260, 260)}
                  alt={file.name}
                  width={260}
                  height={260}
                  unoptimized
                  className={`max-h-24 w-auto object-contain ${file.type === 'image/svg+xml' || file.path.toLowerCase().endsWith('.svg') ? 'max-w-24' : ''}`}
                />
              </TooltipTrigger>
              <TooltipContent className="text-center">
                <p>{file.name}</p>
                <p>{file.type}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </PhotoView>
      ) : (
        <div className="bg-muted/20 mb-1 flex h-24 flex-col items-center justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-muted-foreground flex flex-col items-center justify-center">
                <div className="w-10">
                  <ContentFileIcon file={file} />
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="text-center">
              <p>{file.name}</p>
              <p>{file.type}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      <div className="truncate text-xs" title={file.name}>
        {file.name}
      </div>
      <div className="text-muted-foreground text-xs">{formatFileSize(file.size)}</div>

      {allowMainSelection && (
        <div className="mt-1 flex items-center justify-center">
          <button
            type="button"
            onClick={() => onMainSelect && onMainSelect(file)}
            className={`rounded px-2 py-1 text-xs ${
              isMain ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
            }`}>
            {isMain ? t('main_picture') : t('set_as_main_picture')}
          </button>
        </div>
      )}
    </div>
  )
}

const FileSelector = ({
  multiple = false,
  onlyImages = false,
  types = [],
  optimizeImageSelection = true,
  value,
  onChange,
  buttonLabel,
  buttonLabelChange,
  noSelectionText,
  className = '',
  maxSelectionLimit,
  allowMainSelection = false,
  initialMainId
}: PropsType) => {
  const t = useTranslations()

  // State for modal visibility
  const [modalOpen, setModalOpen] = useState(false)

  // Automatically detect if selected file(s) are SVG and disable optimization
  const isSvgFile = (() => {
    if (!value) {
      return false
    }
    const fileToCheck = Array.isArray(value) ? value[0] : value
    return fileToCheck && (fileToCheck.type === 'image/svg+xml' || fileToCheck.path.toLowerCase().endsWith('.svg'))
  })()

  // Override optimizeImageSelection if SVG is selected
  const shouldOptimizeImage = optimizeImageSelection && !isSvgFile

  // Set up sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Determine the current selection state
  let selectedFiles = multiple ? (Array.isArray(value) ? value : []) : value ? [value as FileType] : []

  // Apply initialMainId if provided and allowMainSelection is enabled
  if (multiple && allowMainSelection && initialMainId && selectedFiles.length > 0) {
    // Check if any file already has isMain set
    const hasMainFile = selectedFiles.some(file => file.isMain)

    // Only apply initialMainId if no file is already marked as main
    if (!hasMainFile) {
      selectedFiles = selectedFiles.map(file => ({
        ...file,
        isMain: file.id === initialMainId
      }))
    }
  }

  // Handle opening the file selection modal
  const handleSelectFile = () => {
    setModalOpen(true)
  }

  // Handle file selection from the modal
  const handleFileSelect = (files: FileType[]) => {
    if (multiple) {
      onChange(files)
    } else {
      onChange(files.length > 0 ? files[0] : null)
    }
  }

  // Remove a file from selection (for multiple files)
  const removeFile = (file: FileType) => {
    if (multiple && Array.isArray(value)) {
      onChange(value.filter(f => f.id !== file.id))
    } else {
      onChange(null)
    }
  }

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id && multiple && Array.isArray(value)) {
      const oldIndex = value.findIndex(file => file.id === active.id)
      const newIndex = value.findIndex(file => file.id === over.id)

      const newOrder = arrayMove(value, oldIndex, newIndex)
      onChange(newOrder)
    }
  }

  // Render a single file preview
  const renderSingleFile = (file: FileType | null, onRemove: () => void) => {
    if (!file) {
      return (
        <div className="text-muted-foreground text-sm">
          {noSelectionText || (onlyImages ? t('no_image_selected') : t('no_file_selected'))}
        </div>
      )
    }

    return (
      <div className="relative rounded-md border p-2" style={{ width: '160px' }}>
        <button
          type="button"
          className="bg-background hover:bg-muted absolute top-1 right-1 z-10 rounded-full p-1 shadow-sm"
          onClick={onRemove}>
          <XIcon className="h-4 w-4" />
        </button>
        <PhotoProvider maskOpacity={0.5}>
          {file.type.startsWith('image/') ? (
            <PhotoView src={thumbUrl(file, 800, 600)}>
              <div className="bg-checkerboard mb-1 flex h-24 cursor-pointer items-center justify-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Image
                      src={thumbUrl(file, 260, 260)}
                      alt={file.name}
                      width={260}
                      height={260}
                      unoptimized
                      className={`max-h-24 w-auto object-contain ${file.type === 'image/svg+xml' || file.path.toLowerCase().endsWith('.svg') ? 'max-w-24' : ''}`}
                    />
                  </TooltipTrigger>
                  <TooltipContent className="text-center">
                    <p>{file.name}</p>
                    <p>{file.type}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </PhotoView>
          ) : (
            <div className="bg-muted/20 mb-1 flex h-24 flex-col items-center justify-center rounded">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-muted-foreground flex flex-col items-center justify-center">
                    <div className="w-10">
                      <ContentFileIcon file={file} />
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-center">
                  <p>{file.name}</p>
                  <p>{file.type}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </PhotoProvider>
        <div className="truncate text-xs" title={file.name}>
          {file.name}
        </div>
        <div className="text-muted-foreground text-xs">{formatFileSize(file.size)}</div>
      </div>
    )
  }

  // Handle setting a file as main
  const handleSetMainFile = (file: FileType) => {
    if (multiple && Array.isArray(value)) {
      // Create a new array with updated isMain values
      const updatedFiles = value.map(f => ({
        ...f,
        isMain: f.id === file.id
      }))

      onChange(updatedFiles)
    }
  }

  // Render multiple files
  const renderMultipleFiles = (files: FileType[]) => {
    if (files.length === 0) {
      return (
        <div className="text-muted-foreground text-sm">
          {noSelectionText || (onlyImages ? t('no_images_selected') : t('no_files_selected'))}
        </div>
      )
    }

    // If there's only one file, no need for drag-and-drop
    if (files.length === 1) {
      return (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8">
          <div
            key={files[0].id}
            className={`relative rounded-md border p-2 ${files[0].isMain ? 'border-primary ring-primary ring-1' : ''}`}>
            <button
              type="button"
              className="bg-background hover:bg-muted absolute top-1 right-1 z-10 rounded-full p-1 shadow-sm"
              onClick={() => removeFile(files[0])}>
              <XIcon className="h-4 w-4" />
            </button>

            <PhotoProvider maskOpacity={0.5}>
              {files[0].type.startsWith('image/') ? (
                <PhotoView src={thumbUrl(files[0], 800, 600)}>
                  <div className="bg-checkerboard mb-1 flex h-24 cursor-pointer items-center justify-center">
                    <Image
                      src={thumbUrl(files[0], 260, 260)}
                      alt={files[0].name}
                      width={260}
                      height={260}
                      unoptimized
                      className={`max-h-24 w-auto object-contain ${files[0].type === 'image/svg+xml' || files[0].path.toLowerCase().endsWith('.svg') ? 'max-w-24' : ''}`}
                    />
                  </div>
                </PhotoView>
              ) : (
                <div className="bg-muted/20 mb-1 flex h-24 flex-col items-center justify-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-muted-foreground flex flex-col items-center justify-center">
                        <div className="w-10">
                          <ContentFileIcon file={files[0]} />
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="text-center">
                      <p>{files[0].name}</p>
                      <p>{files[0].type}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </PhotoProvider>
            <div className="truncate text-xs" title={files[0].name}>
              {files[0].name}
            </div>
            <div className="text-muted-foreground text-xs">{formatFileSize(files[0].size)}</div>

            {allowMainSelection && (
              <div className="mt-1 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => handleSetMainFile(files[0])}
                  className={`rounded px-2 py-1 text-xs ${
                    files[0].isMain ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
                  }`}>
                  {files[0].isMain ? t('main_picture') : t('set_as_main_picture')}
                </button>
              </div>
            )}
          </div>
        </div>
      )
    }

    // For multiple files, enable drag-and-drop
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={files.map(file => file.id)} strategy={verticalListSortingStrategy}>
          <PhotoProvider maskOpacity={0.5}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8">
              {files.map(file => (
                <SortableItem
                  key={file.id}
                  file={file}
                  onRemove={removeFile}
                  allowMainSelection={allowMainSelection}
                  onMainSelect={handleSetMainFile}
                  isMain={!!file.isMain}
                />
              ))}
            </div>
          </PhotoProvider>
        </SortableContext>
      </DndContext>
    )
  }

  // Get appropriate button label
  const getButtonLabel = () => {
    if (multiple) {
      const hasFiles = Array.isArray(value) && value.length > 0
      if (buttonLabelChange && hasFiles) {
        return buttonLabelChange
      }
      if (buttonLabel) {
        return buttonLabel
      }
      return hasFiles
        ? onlyImages
          ? t('change_pictures')
          : t('change_files')
        : onlyImages
          ? t('select_pictures')
          : t('select_files')
    } else {
      const hasFile = value !== null
      if (buttonLabelChange && hasFile) {
        return buttonLabelChange
      }
      if (buttonLabel) {
        return buttonLabel
      }
      return hasFile
        ? onlyImages
          ? t('change_picture')
          : t('change_file')
        : onlyImages
          ? t('select_picture')
          : t('select_file')
    }
  }

  return (
    <div className={className}>
      <div className="mb-2">
        {multiple
          ? renderMultipleFiles(selectedFiles)
          : renderSingleFile(selectedFiles[0] || null, () => onChange(null))}
      </div>
      <Button type="button" onClick={handleSelectFile}>
        {getButtonLabel()}
      </Button>

      {/* File Manager Modal */}
      <FileManager
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSelect={handleFileSelect}
        selectedFiles={selectedFiles}
        multiple={multiple}
        onlyImages={onlyImages}
        optimizeImageSelection={shouldOptimizeImage}
        types={types}
        closeOnSelect={false}
        maxSelectionLimit={maxSelectionLimit}
      />
    </div>
  )
}

export default FileSelector
